import React, { useEffect, useState } from 'react';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '../../../../components/bootstrap/Modal';
import FormGroup from '../../../../components/bootstrap/forms/FormGroup';
import Select from '../../../../components/bootstrap/forms/Select';
import Input from '../../../../components/bootstrap/forms/Input';
import Button, { ButtonGroup } from '../../../../components/bootstrap/Button';
import { centralApi } from '../../../AxiosInstance/CreateInstance';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import CheckboxDropdown from '../../../../components/bootstrap/forms/CheckboxDropdown';

interface InterviewDetail {
    roundType: string;
    scheduledTime?: string;
    interviewLength?: string;
    interviewers?: {
        id: string;
        name: string;
    }[];
}

interface ScheduleInterviewModalProps {
    shortModal: boolean;
    setShortModal: (open: boolean) => void;
    shortFormik: any;
    intDetail: InterviewDetail[];
    themeStatus: 'light' | 'dark';
    singleCandidate: any;
    loading: boolean;
    isReschedule?: boolean;
    selectedInterview?: any;
}
interface Stage {
    roundType: string;
    result?: string | null;
}

const ScheduleInterviewModal: React.FC<ScheduleInterviewModalProps> = ({
    shortModal,
    setShortModal,
    shortFormik,
    singleCandidate = null,
    intDetail,
    loading,
    themeStatus,
    isReschedule = false,
    selectedInterview = null,
}) => {
    const { resetForm, setFieldValue, values } = shortFormik;
    const [availableRounds, setAvailableRounds] = useState<string[]>([]);
    const [availabilityMessage, setAvailabilityMessage] = useState('');
    const [canSchedule, setCanSchedule] = useState(true);
    const { id: requisitionId } = useParams();
    const [checkingAvailability, setCheckingAvailability] = useState(false);

    const [interviewType, setInterviewType] = useState('Human');
    const [templates, setTemplates] = useState<{ _id: string; name: string }[]>([]);
    const [removedInterviewers, setRemovedInterviewers] = useState<string[]>([]);
    const getButtonText = () => {
        if (loading && isReschedule) return 'Rescheduling...';
        if (loading && !isReschedule) return 'Scheduling...';
        if (isReschedule) return 'Reschedule';
        return 'Schedule';
    };

    const buttonText = getButtonText();

    useEffect(() => {
        if (!singleCandidate || !shortModal) return;
        const roundOrder = ['L1', 'L2', 'HR'];
        const fetchHistory = async () => {
            try {
                const res = await centralApi.get(
                    `/interviewService/stages/candidate-applications/${singleCandidate.candidate.id}/history`
                );
                const app = res.data?.applications?.find(
                    (a: any) => a.requisitionID === requisitionId
                );
                const stages = app?.stages || [];
                const validRounds: string[] = Array.from(
                    new Set(stages
                        .filter((s: any) => s.roundType && s.roundType !== '-')
                        .map((s: any) => s.roundType.trim().toUpperCase()))
                );
                const completedRounds = stages
                    .filter((r: Stage) => ['shortlisted', 'rejected'].includes((r.result || '').toLowerCase()))
                    .map((r: Stage) => r.roundType.trim().toUpperCase());

                const nextRound = roundOrder.find(r => validRounds.includes(r) && !completedRounds.includes(r));
                setAvailableRounds(nextRound ? [nextRound] : []);
            } catch (err) {
                console.error('Error fetching candidate history', err);
            }
        };
        fetchHistory();
    }, [singleCandidate, requisitionId, shortModal]);

    useEffect(() => {
        if (!shortModal) {
            setAvailableRounds([]);
            setAvailabilityMessage('');
            setCanSchedule(true);
            setInterviewType('Human');

            resetForm();
        }
    }, [shortModal, resetForm]);

    useEffect(() => {
        if (shortModal && availableRounds.length > 0) {
            setFieldValue('roundTypes', availableRounds[0]);
        }
    }, [availableRounds, shortModal, setFieldValue]);


    useEffect(() => {

        const checkAvailability = async () => {

            const round = values.roundTypes;

            const selectedRounds = intDetail.find(
                (item) => item.roundType === values.roundTypes
            );

            const validPanelInterviewers =
                selectedRounds?.interviewers?.map((i: any) => i.id) || [];

            const interviewerId = (values.interviewerId || []).filter(
                (id: string) => validPanelInterviewers.includes(id)
            );

            const date = values.scheduleTimeFrom;

            // RESCHEDULE => selected time
            // SCHEDULE => panel configured time
            const selectedTime = isReschedule
                ? values.scheduledTime
                : selectedRounds?.scheduledTime;

            if (
                !round ||
                !interviewerId.length ||
                !date ||
                !selectedTime
            ) {
                setAvailabilityMessage('');
                setCanSchedule(false);
                return;
            }

            const selectedRound = intDetail.find(
                (item) => item.roundType === round
            );

            if (!selectedRound?.interviewLength) {
                return;
            }

            try {
                setCheckingAvailability(true);

                const [hours, minutes] = selectedTime.split(':');

                const duration = Number(
                    selectedRound.interviewLength
                );

                const startDate = new Date(date);

                startDate.setHours(
                    Number(hours),
                    Number(minutes),
                    0,
                    0
                );

                const endDate = new Date(
                    startDate.getTime() + duration * 60000
                );

                const now = new Date();

                // FUTURE TIME VALIDATION
                if (startDate.getTime() < now.getTime()) {

                    setAvailabilityMessage(
                        isReschedule
                            ? 'Please select a future time.'
                            : 'You cannot schedule an interview in the past.'
                    );

                    setCanSchedule(false);

                    return;
                }

                const res = await centralApi.post(
                    '/interviewService/interview_service/interviewers/availability',
                    {
                        interviewerIds: interviewerId,
                        startDate: startDate.toISOString(),
                        endDate: endDate.toISOString(),
                    }
                );

                const availabilityData =
                    res?.data?.data?.value || [];

                const busyInterviewers =
                    availabilityData.filter((item: any) => {

                        const scheduleItems =
                            item.scheduleItems || [];

                        return scheduleItems.some((schedule: any) =>
                            ['busy', 'tentative'].includes(
                                schedule.status?.toLowerCase()
                            )
                        );
                    });

                const isBusy =
                    busyInterviewers.length > 0;

                if (isBusy) {

                    const busyNames = busyInterviewers
                        .map((item: any) => item.scheduleId)
                        .join(', ');

                    setAvailabilityMessage(
                        `${busyNames} ${busyInterviewers.length > 1
                            ? 'are'
                            : 'is'
                        } busy for this slot. Do you still want to continue?`
                    );

                    setCanSchedule(true);

                } else {

                    setAvailabilityMessage('');
                    setCanSchedule(true);
                }

            } catch (err: any) {

                setAvailabilityMessage(
                    'Error checking availability. Please try again.'
                );

                setCanSchedule(false);
            } finally {
                setCheckingAvailability(false);
            }
        };

        checkAvailability();

    }, [
        values.roundTypes,
        values.interviewerId,
        values.scheduleTimeFrom,
        values.scheduledTime,
        intDetail,
        isReschedule
    ]);


    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const res = await centralApi.get('/userService/questionTemplate/get?pageNo=1&limit=2');
                if (res.data.status === 200) {
                    setTemplates(res.data.data);
                }
            } catch (err) {
                // console.error('Failed to fetch templates:', err);
            }
        };

        fetchTemplates();
    }, []);


    useEffect(() => {
        if (!shortModal) {
            resetForm();
            setInterviewType('Human');
            setAvailableRounds([]);
            setRemovedInterviewers([]);
            return;
        }

        if (isReschedule && selectedInterview) {
            const interviewers =
                selectedInterview.interviewers?.map((i: any) => i.id) || [];

            shortFormik.setValues({
                ...shortFormik.values,
                roundTypes: selectedInterview.roundType || '',
                scheduleTimeFrom: selectedInterview.scheduledTime
                    ? dayjs(selectedInterview.scheduledTime).format('YYYY-MM-DD')
                    : '',
                scheduledTime: selectedInterview.scheduledTime
                    ? dayjs(selectedInterview.scheduledTime).format('HH:mm')
                    : '',
                interviewerId: interviewers,
                durationMinutes: selectedInterview.panel?.interviewLength || 45,
                interviewType: 'Human',
                templateId: '',
            });

            setInterviewType('Human');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shortModal, isReschedule, selectedInterview,]);

    useEffect(() => {
        if (!selectedInterview || !shortModal) return;

        const previous = selectedInterview.interviewers?.map((i: any) => i.name) || [];

        const currentRound = intDetail.find(
            (item) => item.roundType === shortFormik.values.roundTypes
        );

        const current = currentRound?.interviewers?.map((i: any) => i.name) || [];

        const removed = previous.filter((name: string) => !current.includes(name));

        setRemovedInterviewers(removed);
    }, [
        selectedInterview,
        shortModal,
        shortFormik.values.roundTypes,
        intDetail
    ]);
    const hasInterviewer =
        Array.isArray(values.interviewerId) &&
        values.interviewerId.length > 0;
    return (
        <Modal size="lg" isCentered setIsOpen={setShortModal} isOpen={shortModal}>
            <ModalHeader>
                <ModalTitle id="shortlist-candidate">
                    {isReschedule
                        ? 'Reschedule Interview'
                        : 'Schedule Interview'}
                </ModalTitle>
            </ModalHeader>

            <ModalBody>
                <form onSubmit={shortFormik.handleSubmit}>
                    {/* Round Type */}
                    <div className="row g-4 mb-4  justify-content-center">
                        <div className="col-md-6">

                            <FormGroup id="round-type" label="Round Type">
                                <Select
                                    id="roundTypes"
                                    name="roundTypes"
                                    ariaLabel="roundTypes"
                                    placeholder="Select Round Type"
                                    value={values.roundTypes}
                                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                        shortFormik.handleChange(e);
                                        shortFormik.setFieldValue('interviewerId', []);

                                        const selectedRound = e.target.value;


                                        if (selectedRound === "L1") {
                                            // keep the selected interviewType (AI or Human)
                                        } else {
                                            // FORCE HUMAN for L2 / HR rounds
                                            setInterviewType("Human");
                                            shortFormik.setFieldValue("interviewType", "Human");
                                            shortFormik.setFieldValue("templateId", "");
                                            shortFormik.setFieldValue("interviewerId", "");
                                        }
                                    }}

                                    list={availableRounds.map(round => ({
                                        value: round,
                                        text: round,
                                    }))}
                                    onBlur={shortFormik.handleBlur}
                                    isTouched={!!shortFormik.touched.roundTypes}
                                    invalidFeedback={shortFormik.errors.roundTypes}
                                    isValid={shortFormik.isValid && !shortFormik.errors.roundTypes}
                                    disabled={availableRounds.length === 0}
                                />
                            </FormGroup>
                        </div>
                    </div>

                    {/* Interview Type Toggle — only visible for L1 */}
                    {values.roundTypes === 'L1' ? (
                        <>
                            <div className="row justify-content-center mb-4">
                                <div className="col-md-6 justify-content-center">
                                    <ButtonGroup>
                                        <Button isDisable color="dark" isLight>
                                            Interview Type:
                                        </Button>

                                        <Button
                                            color={interviewType === 'AI' ? 'success' : 'dark'}
                                            isLight={interviewType !== 'AI'}
                                            onClick={() => {
                                                setInterviewType('AI');
                                                shortFormik.setFieldValue('interviewType', 'AI');
                                                shortFormik.setFieldValue('interviewerId', '');
                                                shortFormik.setFieldValue('templateId', '');

                                            }}
                                            aria-label="AI Voice Interview"
                                        >
                                            AI Voice Interview
                                        </Button>

                                        <Button
                                            icon="User"
                                            color={interviewType === 'Human' ? 'success' : 'dark'}
                                            isLight={interviewType !== 'Human'}
                                            onClick={() => {
                                                setInterviewType('Human');
                                                shortFormik.setFieldValue('interviewType', 'Human');
                                                shortFormik.setFieldValue('interviewerId', '');
                                                shortFormik.setFieldValue('templateId', '');

                                            }}
                                            aria-label="Human Interviewer"
                                        >
                                            Human Interviewer
                                        </Button>
                                    </ButtonGroup>
                                </div>
                            </div>
                            {/* Conditional Dropdowns for AI or Human */}
                            {interviewType === 'Human' ? (
                                <div className="row justify-content-center ">
                                    <div className="col-md-6 justify-content-center">
                                        <FormGroup id="interviewerId" label="Interviewers">
                                            <CheckboxDropdown
                                                id="interviewerId"
                                                label=""
                                                options={(() => {
                                                    const selectedRound = intDetail.find(
                                                        (item) => item.roundType === shortFormik.values.roundTypes
                                                    );

                                                    if (selectedRound && Array.isArray(selectedRound.interviewers)) {
                                                        return selectedRound.interviewers.map((user) => ({
                                                            value: user.id,
                                                            text: user.name,
                                                        }));
                                                    }

                                                    return [];
                                                })()}
                                                value={shortFormik.values.interviewerId || []}
                                                onChange={(vals: string[]) =>
                                                    shortFormik.setFieldValue('interviewerId', vals)
                                                }
                                                placeholder={(() => {
                                                    const selectedRound = intDetail.find(
                                                        (item) => item.roundType === shortFormik.values.roundTypes
                                                    );

                                                    if (!selectedRound || !selectedRound.interviewers?.length) {
                                                        return 'No Interviewers Assigned for this Round';
                                                    }

                                                    return 'Select Interviewers';
                                                })()}
                                                disabled={
                                                    !intDetail.find(
                                                        (item) => item.roundType === shortFormik.values.roundTypes
                                                    )?.interviewers?.length
                                                }
                                                isTouched={!!shortFormik.touched.interviewerId}
                                                invalidFeedback={
                                                    typeof shortFormik.errors.interviewerId === 'string'
                                                        ? shortFormik.errors.interviewerId
                                                        : undefined
                                                }
                                                isValid={
                                                    shortFormik.isValid && !shortFormik.errors.interviewerId
                                                }
                                            />
                                        </FormGroup>

                                    </div>
                                </div>
                            ) : (
                                <div className="row justify-content-center mb-4">
                                    <div className="col-md-6 justify-content-center">
                                        <FormGroup id="templateId" label="Question Template">
                                            <Select
                                                id="templateId"
                                                name="templateId"
                                                ariaLabel="templateId"
                                                placeholder="Select Question Template"
                                                value={shortFormik.values.templateId}
                                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                                                    shortFormik.handleChange(e);
                                                    const selectedId = e.target.value;
                                                    shortFormik.setFieldValue('templateId', selectedId);

                                                }}
                                                onBlur={shortFormik.handleBlur}
                                                list={templates.map((item) => ({
                                                    value: item._id,
                                                    text: item.name,
                                                }))}
                                                isTouched={!!shortFormik.touched.templateId}
                                                invalidFeedback={shortFormik.errors.templateId}
                                                isValid={shortFormik.isValid && !shortFormik.errors.templateId}
                                            />
                                        </FormGroup>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="row justify-content-center mb-4">
                            <div className="col-md-6">
                                <FormGroup id="interviewerId" label="Interviewers">
                                    <CheckboxDropdown
                                        id="interviewerId"
                                        label=""
                                        options={(() => {
                                            const selectedRound = intDetail.find(
                                                (item) => item.roundType === shortFormik.values.roundTypes
                                            );

                                            if (selectedRound && Array.isArray(selectedRound.interviewers)) {
                                                return selectedRound.interviewers.map((user) => ({
                                                    value: user.id,
                                                    text: user.name,
                                                }));
                                            }

                                            return [];
                                        })()}
                                        value={shortFormik.values.interviewerId || []}
                                        onChange={(vals: string[]) =>
                                            shortFormik.setFieldValue('interviewerId', vals)
                                        }
                                        placeholder={(() => {
                                            const selectedRound = intDetail.find(
                                                (item) => item.roundType === shortFormik.values.roundTypes
                                            );

                                            if (!selectedRound || !selectedRound.interviewers?.length) {
                                                return 'No Interviewers Assigned for this Round';
                                            }

                                            return 'Select Interviewers';
                                        })()}
                                        disabled={
                                            !intDetail.find(
                                                (item) => item.roundType === shortFormik.values.roundTypes
                                            )?.interviewers?.length
                                        }
                                        isTouched={!!shortFormik.touched.interviewerId}
                                        invalidFeedback={
                                            typeof shortFormik.errors.interviewerId === 'string'
                                                ? shortFormik.errors.interviewerId
                                                : undefined
                                        }
                                        isValid={
                                            shortFormik.isValid && !shortFormik.errors.interviewerId
                                        }
                                    />
                                </FormGroup>

                            </div>

                        </div>
                    )}
                    {removedInterviewers.length > 0 && (
                        <div className="text-warning text-center mb-4 ">
                            Previously selected interviewer
                            {removedInterviewers.length > 1 ? 's are' : ' is'} no longer attached
                            to this panel:
                            <strong> {removedInterviewers.join(', ')}</strong>
                        </div>
                    )}


                    {/* Schedule Date */}
                    <div className="row g-4 mb-4 justify-content-center">
                        <div className="col-md-6">
                            <FormGroup id="scheduleTimeFrom" label="Schedule Date">
                                <Input
                                    type="date"
                                    id="scheduleTimeFrom"
                                    className="form-control py-2 bg-dark"
                                    placeholder="Enter Schedule Date"
                                    value={shortFormik.values.scheduleTimeFrom}
                                    onChange={shortFormik.handleChange}
                                    onBlur={shortFormik.handleBlur}
                                    isTouched={!!shortFormik.touched.scheduleTimeFrom}
                                    invalidFeedback={shortFormik.errors.scheduleTimeFrom}
                                    isValid={shortFormik.isValid && !shortFormik.errors.scheduleTimeFrom}
                                    name="scheduleTimeFrom"
                                    min={dayjs().format('YYYY-MM-DD')}
                                />
                            </FormGroup>
                        </div>
                    </div>
                    {availabilityMessage && !isReschedule && (
                        <div className="text-danger text-center mt-2">
                            {availabilityMessage}
                        </div>
                    )}

                    {/* Reschedule Time */}
                    {isReschedule && (
                        <div className="row g-4 mb-4 justify-content-center">
                            <div className="col-md-6">
                                <FormGroup
                                    id="scheduledTime"
                                    label="Schedule Time"
                                >
                                    <Input
                                        type="time"
                                        id="scheduledTime"
                                        name="scheduledTime"
                                        className="form-control py-2"
                                        value={shortFormik.values.scheduledTime}
                                        onChange={shortFormik.handleChange}
                                        onBlur={shortFormik.handleBlur}
                                        isTouched={
                                            !!shortFormik.touched.scheduledTime
                                        }
                                        invalidFeedback={
                                            shortFormik.errors.scheduledTime
                                        }
                                        isValid={
                                            shortFormik.isValid &&
                                            !shortFormik.errors.scheduledTime
                                        }
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    )}
                    {availabilityMessage && isReschedule && (
                        <div className="text-danger text-center mt-2">
                            {availabilityMessage}
                        </div>
                    )}
                    {checkingAvailability
                        && (
                            <div className="text-danger text-center mt-2">
                                Checking Availability...
                            </div>
                        )}
                    {/* Actions */}
                    <div className="d-flex justify-content-end gap-3 mt-5 ">
                        <Button
                            color="light"
                            className="px-4 py-2 bg-dark text-white"
                            onClick={() => setShortModal(false)}

                        >
                            Cancel
                        </Button>

                        <Button
                            type="submit"
                            className="px-4 py-2 text-white hrms-gradient-btn"
                            isDisable={
                                loading ||
                                shortFormik.isSubmitting ||
                                !canSchedule ||
                                !hasInterviewer ||
                                !shortFormik.isValid
                            }
                        >
                            {buttonText}
                        </Button>
                    </div>
                </form>
            </ModalBody>
        </Modal >
    );
};

export default ScheduleInterviewModal;

ScheduleInterviewModal.defaultProps = {
    isReschedule: false,
    selectedInterview: null,
};